import {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import dayjs from 'dayjs';
// import { useAuth } from './AuthContext';
// import { useQuery } from '@tanstack/react-query';
// import { axiosInstance } from '@/lib/axios';
import { SnapsuitePayloadDataType } from '@/components/header/header3';
import { useDateRange } from '@/context/DateRangeContext';
import { useGetRegionWiseAccounts } from '@/lib/api/hooks/useAccounts';
import { useRegion } from './CountryContext';
import { persistGet, persistSet } from '@/lib/utils';
import { toApiDate } from '@/utility/to-api-date';
interface Profile {
  accountId: string;
  id: number;
  profileId: string;
  accountName: string;
  selected: boolean;
}
interface sellerVendorDetails {
  sellerDetails: {
    hasSellerAccess: boolean;
    sellerPartnerIds: string[];
    sellerTokensCount: number;
  };
  vendorDetails: {
    hasVendorAccess: boolean;
    vendorPartnerIds: string[];
    vendorTokensCount: number;
  };
}

interface AdAccount {
  id: number;
  name: string;
  profiles: Profile[];
  selected: boolean;
  sellerVendorDetails: sellerVendorDetails;
}
interface HeaderStateType3 {
  // Page title of the current view
  pageTitle: string;
  setPageTitle: Dispatch<SetStateAction<string>>;

  // Ad accounts with multi-selection state
  adAccounts: AdAccount[];
  setAdAccounts: Dispatch<SetStateAction<AdAccount[]>>;

  // Dropdown visibility for Ad Accounts
  adAccountVisible: boolean;
  setAdAccountVisible: Dispatch<SetStateAction<boolean>>;

  // Date range using native Date objects
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  setDateRange: Dispatch<SetStateAction<[dayjs.Dayjs, dayjs.Dayjs]>>;

  // Date picker popover visibility
  datePickerVisible: boolean;
  setDatePickerVisible: Dispatch<SetStateAction<boolean>>;

  // advertierId
  advertiserId: string[];
  setAdvertiserId: Dispatch<SetStateAction<string[]>>;

  // Current user role (not full user info)
  role: string;
  setRole: Dispatch<SetStateAction<string>>;

  snapsuitepayloaddata: SnapsuitePayloadDataType;
  setSnapsuitepayloaddata: Dispatch<SetStateAction<SnapsuitePayloadDataType>>;
}

// Create the context
export const HeaderState3 = createContext<HeaderStateType3 | undefined>(undefined);

export const HeaderStateProvider3 = ({ children }: { children: ReactNode }) => {
  const { dateRange: ContextDateRange, compareRange } = useDateRange();
  // 1. Page title
  const getPageTitle = () => {
    return 'Dashboard'; // Default title
  };
  const [pageTitle, setPageTitle] = useState(getPageTitle());
  const { selectedRegion } = useRegion();
  const { data: RegionWiseAccount, isFetched: RegionWiseAccountFetched } =
    useGetRegionWiseAccounts(selectedRegion.countrycode!);
  // const { user } = useAuth();

  // const { data: accountsData, isFetched } = useQuery({
  //   queryKey: ['accounts'],
  //   queryFn: async () => {
  //     const { data } = await axiosInstance.get('/accounts');
  //     return data;
  //   },
  //   enabled: !!user,
  // });

  // 2. Ad accounts with multi-selection
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [advertiserId, setAdvertiserId] = useState<string[]>([]);
  const [adAccountVisible, setAdAccountVisible] = useState(false);
  const [snapsuitepayloaddata, setSnapsuitepayloaddata] =
    useState<SnapsuitePayloadDataType>({
      selling_partner_ids: [],
      profile_ids: [],
      startDate: '',
      endDate: '',
      compareStartDate: '',
      compareEndDate: '',
    });

  // 3. Date range
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  // 4. User role
  const [role, setRole] = useState('Manager');

  useEffect(() => {
    if (RegionWiseAccountFetched && RegionWiseAccount) {
      const accountsWithSelection = RegionWiseAccount.map((account: any) => ({
        ...account,
        selected: false,
        profiles: account.profiles.map((profile: any) => ({
          ...profile,
          selected: false,
        })),
      }));
      // Hydrate selected profiles from storage (if present)
      // Use a separate key to avoid clobbering non-Ads/growth-tracking selections
      const stored = persistGet<string[]>('app_v2_snap_profiles');
      if (stored && Array.isArray(stored) && stored.length > 0) {
        accountsWithSelection.forEach((acc: any) => {
          acc.profiles = acc.profiles.map((p: any) => ({
            ...p,
            selected: stored.includes(p.profileId)
          }));
        });
      }
      setAdAccounts(accountsWithSelection);
    }
  }, [RegionWiseAccountFetched, RegionWiseAccount]);

  // 5. Extract seller and vendor partner IDs from the accounts data
  function extractPartnerIds(data) {
    const sellerPartnerIds: string[] = [];
    const vendorPartnerIds: string[] = [];

    data.forEach((item) => {
      if (item.sellerVendorDetails) {
        const { sellerDetails, vendorDetails } = item.sellerVendorDetails;

        if (sellerDetails?.sellerPartnerIds) {
          sellerPartnerIds.push(...sellerDetails.sellerPartnerIds);
        }

        if (vendorDetails?.vendorPartnerIds) {
          vendorPartnerIds.push(...vendorDetails.vendorPartnerIds);
        }
      }
    });

    return { sellerPartnerIds, vendorPartnerIds };
  }
  useEffect(() => {
    if (RegionWiseAccountFetched && RegionWiseAccount) {
      // Persist currently selected profiles for Snapsuite under a separate key
      const selectedProfileIds = RegionWiseAccount.flatMap((account) =>
        account.profiles.filter((p: any) => p.selected).map((p: any) => p.profileId)
      );
      persistSet('app_v2_snap_profiles', selectedProfileIds);

      const ids = extractPartnerIds(RegionWiseAccount);

      const extractedPartnerIds = [
        ...ids.sellerPartnerIds,
        ...ids.vendorPartnerIds,
      ];
      const startDate = toApiDate(ContextDateRange.from);
      const endDate = toApiDate(ContextDateRange.to);
      const compareStartDate = toApiDate(compareRange.from);
      const compareEndDate = toApiDate(compareRange.to);
      const payloaddata = {
        selling_partner_ids: extractedPartnerIds,
        profile_ids: selectedProfileIds.length > 0 ? selectedProfileIds : RegionWiseAccount.flatMap((account) => account.profiles.map((profile) => profile.profileId)),
        startDate: startDate,
        endDate: endDate,
        compareStartDate: compareStartDate,
        compareEndDate: compareEndDate,
      };

      setSnapsuitepayloaddata(payloaddata);
    }
  }, [
    RegionWiseAccountFetched,
    RegionWiseAccount,
    ContextDateRange.from,
    ContextDateRange.to,
    compareRange.from,
    compareRange.to,
  ]);
  return (
    <HeaderState3.Provider
      value={{
        pageTitle,
        setPageTitle,
        adAccounts,
        setAdAccounts,
        advertiserId,
        setAdvertiserId,
        adAccountVisible,
        setAdAccountVisible,
        dateRange,
        setDateRange,
        datePickerVisible,
        setDatePickerVisible,
        role,
        setRole,
        snapsuitepayloaddata,
        setSnapsuitepayloaddata,
      }}
    >
      {children}
    </HeaderState3.Provider>
  );
};

// Custom hook for consuming header state

export const useHeaderStateProvider3 = () => {
  const context = useContext(HeaderState3);
  if (!context) {
    throw new Error(
      'useHeaderStateProvider3 must be used within HeaderStateProvider3'
    );
  }
  return context;
};
